import {useCallback, useEffect, useRef, useState} from 'react';

import {MarketPlaceTradeStatus} from '@afriex/core/domain/enums';

import {merchantService} from '@/services/merchants';
import type {
  MarketPlaceTrade,
  MarketPlaceTradeQuery,
  MarketPlaceTradeSummary,
} from '@/types/MarketPlace';

import {PAGE_LIMIT} from '../utils/constants';

export const EMPTY_TRADE_SUMMARY: MarketPlaceTradeSummary = {
  tradeCount: 0,
  offerCount: 0,
  counterpartyCount: 0,
  statusCounts: {
    [MarketPlaceTradeStatus.SETTLING]: 0,
    [MarketPlaceTradeStatus.SETTLED]: 0,
    [MarketPlaceTradeStatus.EXPIRED]: 0,
  },
  settledByPair: [],
};

/**
 * Fetches one page of marketplace trades plus the summary over every match.
 * The last good page stays on screen when a request fails; `error` is for an
 * inline retry, not for replacing the table with an empty state.
 */
const useMarketPlaceTrades = (filters: MarketPlaceTradeQuery, page: number) => {
  const [trades, setTrades] = useState<MarketPlaceTrade[]>([]);
  const [summary, setSummary] =
    useState<MarketPlaceTradeSummary>(EMPTY_TRADE_SUMMARY);
  // The status filter `summary` was fetched with, so its label never runs
  // ahead of its numbers while a new filter is loading or has failed.
  const [summaryStatus, setSummaryStatus] = useState<MarketPlaceTradeStatus>();
  const [total, setTotal] = useState(0);
  const [hasLoaded, setHasLoaded] = useState(false);
  // The request the last response or failure answered. Loading and the error
  // are derived from it, so the effect only sets state once a read settles.
  const [settled, setSettled] = useState<{key: string; error: unknown}>();
  const [reloadKey, setReloadKey] = useState(0);

  // Re-applying the same filters makes a new object with the same values, so
  // key on the values and read the latest object from a ref. Load-bearing:
  // the search box clears an absent search on mount, which rewrites the
  // filters object, and keying on the object would fetch twice.
  const filtersKey = JSON.stringify(filters);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const requestKey = `${filtersKey}|${page}|${reloadKey}`;

  useEffect(() => {
    const controller = new AbortController();
    const {signal} = controller;
    const requestFilters = filtersRef.current;
    merchantService
      .getMarketPlaceTrades(
        {...requestFilters, page, limit: PAGE_LIMIT},
        signal,
      )
      .then((result) => {
        // A newer request (or an unmount) superseded this one.
        if (signal.aborted) {
          return;
        }
        setTrades(result.data);
        setSummaryStatus(requestFilters.status);
        setSummary(result.summary ?? EMPTY_TRADE_SUMMARY);
        setTotal(result.total);
        setHasLoaded(true);
        setSettled({key: requestKey, error: null});
      })
      .catch((requestError: unknown) => {
        if (signal.aborted) {
          return;
        }
        setSettled({key: requestKey, error: requestError});
      });

    return () => controller.abort();
  }, [page, requestKey]);

  const retry = useCallback(() => setReloadKey((key) => key + 1), []);
  const isLoading = settled?.key !== requestKey;
  const error = settled?.key === requestKey ? settled.error : null;

  return {
    trades,
    summary,
    summaryStatus,
    total,
    isLoading,
    hasLoaded,
    error,
    retry,
  };
};

export default useMarketPlaceTrades;
